import { useEffect, useRef } from 'react';

function nearestScrollParent(el: Element | null): Element | null {
    let node = el?.parentElement ?? null;
    while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        const overflow = `${style.overflowY} ${style.overflow}`;
        if (/(auto|scroll)/.test(overflow)) return node;
        node = node.parentElement;
    }
    return null;
}

export function useInfiniteScroll({
    hasMore,
    loading,
    disabled = false,
    onLoadMore,
}: {
    hasMore: boolean;
    loading: boolean;
    disabled?: boolean;
    onLoadMore: () => void;
}) {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const onLoadMoreRef = useRef(onLoadMore);
    const armedRef = useRef(true);
    onLoadMoreRef.current = onLoadMore;

    useEffect(() => {
        armedRef.current = !loading;
    }, [loading]);

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasMore || loading || disabled) return;

        const root = nearestScrollParent(node);
        const observer = new IntersectionObserver(
            (entries) => {
                if (!armedRef.current) return;
                if (!entries.some((entry) => entry.isIntersecting)) return;
                armedRef.current = false;
                onLoadMoreRef.current();
            },
            { root, rootMargin: '64px 0px', threshold: 0 }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [disabled, hasMore, loading]);

    return sentinelRef;
}
